import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "hv_cookie_consent";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    try { if (!localStorage.getItem(KEY)) setOpen(true); } catch { setOpen(true); }
    const reopen = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
        setPrefs({ analytics: !!saved.analytics, marketing: !!saved.marketing });
      } catch (e) { console.error("Cookie preferences unreadable", e); }
      setCustom(true); setOpen(true);
    };
    window.addEventListener("hv:open-cookies", reopen);
    return () => window.removeEventListener("hv:open-cookies", reopen);
  }, []);

  const persist = (value) => {
    try { localStorage.setItem(KEY, JSON.stringify({ ...value, necessary: true, ts: Date.now() })); } catch (e) { console.error("Cookie preferences not saved", e); }
    setOpen(false); setCustom(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6" data-testid="cookie-banner">
      <div className="max-w-[1100px] mx-auto bg-[#0a1b33] text-white shadow-2xl border border-white/10">
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <Cookie size={22} className="text-[#a9b4ff] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-serif text-xl md:text-2xl font-semibold">Usamos cookies</h3>
              <p className="text-white/70 text-sm mt-2 leading-relaxed max-w-2xl">
                Utilizamos cookies propias y de terceros para mejorar tu experiencia y analizar el uso de la web.
                Puedes aceptarlas todas, rechazarlas o elegir qué cookies permites. Consulta nuestra{" "}
                <Link to="/politica-de-cookies" className="underline hover:text-white">Política de Cookies</Link>.
              </p>

              {custom && (
                <div className="mt-5 space-y-3" data-testid="cookie-prefs">
                  <Row title="Necesarias" desc="Imprescindibles para el funcionamiento del sitio. Siempre activas." checked disabled />
                  <Row title="Analíticas" desc="Nos ayudan a entender cómo se usa la web." checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testId="cookie-analytics" />
                  <Row title="Marketing" desc="Permiten mostrar contenido y campañas relevantes." checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testId="cookie-marketing" />
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                <button onClick={() => persist({ analytics: true, marketing: true })} className="bg-white text-[#0a1b33] px-5 py-2.5 text-xs font-semibold tracking-[0.12em] uppercase hover:bg-[#a9b4ff] hover:text-white transition-colors" data-testid="cookie-accept-all">Aceptar todas</button>
                <button onClick={() => persist({ analytics: false, marketing: false })} className="border border-white/40 px-5 py-2.5 text-xs font-semibold tracking-[0.12em] uppercase hover:bg-white/10 transition-colors" data-testid="cookie-reject-all">Rechazar todas</button>
                {custom ? (
                  <button onClick={() => persist(prefs)} className="border border-[#a9b4ff] text-[#a9b4ff] px-5 py-2.5 text-xs font-semibold tracking-[0.12em] uppercase hover:bg-[#a9b4ff] hover:text-[#0a1b33] transition-colors" data-testid="cookie-save-prefs">Guardar preferencias</button>
                ) : (
                  <button onClick={() => setCustom(true)} className="border border-white/40 px-5 py-2.5 text-xs font-semibold tracking-[0.12em] uppercase hover:bg-white/10 transition-colors" data-testid="cookie-customize">Configurar</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ title, desc, checked, disabled, onChange, testId }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} className="accent-[#a9b4ff] mt-1" data-testid={testId} />
      <span>
        <span className="block text-sm font-medium text-white">{title}</span>
        <span className="block text-xs text-white/55">{desc}</span>
      </span>
    </label>
  );
}
